import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { microBrand } from './brand';
import './section-rail.scss';
const TWIG_HEIGHT = 12;
const LINE_TOP = 6;
/**
 * Section menu with a quiet branch line to the active item: the React Bits Branched Menu idea applied
 * to an existing in-page section list. Keeps the page's own state; no routing or navigation change.
 */
export function SectionRail({ items, value, onChange, ariaLabel }) {
    const navRef = useRef(null);
    const trunkRef = useRef(null);
    const twigRef = useRef(null);
    const buttonRefs = useRef([]);
    const activeIndex = items.findIndex((item) => item.value === value);
    const activeIndexRef = useRef(activeIndex);
    activeIndexRef.current = activeIndex;
    const place = () => {
        const nav = navRef.current;
        const trunk = trunkRef.current;
        const twig = twigRef.current;
        if (!nav || !trunk || !twig)
            return;
        const button = buttonRefs.current[activeIndexRef.current];
        if (!button) {
            trunk.style.height = '0px';
            twig.style.opacity = '0';
            return;
        }
        const navRect = nav.getBoundingClientRect();
        const rect = button.getBoundingClientRect();
        const middle = rect.top - navRect.top + rect.height / 2;
        trunk.style.height = `${Math.max(0, middle - TWIG_HEIGHT - LINE_TOP)}px`;
        twig.style.transform = `translateY(${middle - TWIG_HEIGHT}px)`;
        twig.style.opacity = '1';
    };
    useLayoutEffect(() => {
        place();
        const nav = navRef.current;
        if (!nav || nav.hasAttribute('data-ready'))
            return undefined;
        const frame = requestAnimationFrame(() => nav.setAttribute('data-ready', ''));
        return () => cancelAnimationFrame(frame);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, items.length]);
    useEffect(() => {
        const nav = navRef.current;
        if (!nav || typeof ResizeObserver === 'undefined')
            return undefined;
        const observer = new ResizeObserver(() => place());
        observer.observe(nav);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const style = {
        '--sr-branch': microBrand.accentMark,
        '--sr-hover': microBrand.hover,
        '--sr-focus': microBrand.focus
    };
    return (_jsxs("nav", { ref: navRef, className: 'section-rail', "aria-label": ariaLabel, style: style, children: [_jsx("span", { className: 'section-rail__line', "aria-hidden": 'true' }), _jsx("span", { ref: trunkRef, className: 'section-rail__trunk', "aria-hidden": 'true' }), _jsx("span", { ref: twigRef, className: 'section-rail__twig', "aria-hidden": 'true' }), _jsx("ul", { className: 'section-rail__list', children: items.map((item, index) => {
                    const active = index === activeIndex;
                    return (_jsx("li", { children: _jsxs("button", { ref: (element) => {
                                buttonRefs.current[index] = element;
                            }, type: 'button', className: 'section-rail__item', "aria-current": active ? 'true' : undefined, onClick: () => onChange(item.value), children: [_jsx("span", { className: 'section-rail__label', children: item.label }), item.count !== undefined ? (_jsxs("span", { className: 'section-rail__count', children: [_jsx("span", { className: 'section-rail__sr', children: ", " }), item.count] })) : null] }) }, String(item.value)));
                }) })] }));
}
